import { selectEmployees } from '../../employees/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { selectCurrentEmployee, selectTodaysTaskCompletions } from '../reducer';
import { TaskListItem } from '../components/TaskListItem';
import EmployeeDropdown from '../../../core/components/EmployeeDropdown';
import { TaskCompletion } from '../models';
import { useState, useEffect } from 'react';
import { getLastFourHoursTasks, getTodaysTaskCompletions, setCurrentEmployee } from '../actions';
import { getTodaysTaskCompletionsDefaultQuery } from '../helpers';
import { getApiStatus } from '../../../core/utils';
import Loader from '../../../core/components/Loader';
import HourlyChecklist from '../components/HourlyChecklist';
import { LastFourHoursTasks, HourlyViewMode } from '../components/HourlyChecklist';
import { fetchEmployees } from '../../employees/actions';
import { FilterSection } from '../../../core/components/FilterSection';
import { EmployeeInitialsDropdown } from '../../../core/components/EmployeeInitials';

const TodaysTasksNew = ({
  lastFourHoursTasks,
  primarySite,
}: {
  lastFourHoursTasks: LastFourHoursTasks;
  primarySite: any;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [typeFilter, setTypeFilter] = useState('OpenChecklist');
  const employees = useSelector(selectEmployees);
  const currentEmployeeId = useSelector(selectCurrentEmployee);
  const tasks = useSelector(selectTodaysTaskCompletions);
  const [dailyTasks, setDailyTasks] = useState<TaskCompletion[]>([]);
  const [hourlyTasks, setHourlyTasks] = useState<TaskCompletion[]>([]);
  const [toggledTask, setToggledTask] = useState<number | null>(null);
  const [tempEmployee, setTempEmployee] = useState<number | null>(null);
  const loadingTasks = useSelector((state) => getApiStatus(state, 'GET_TODAYS_TASK_COMPLETIONS'));
  const [hourlyViewMode, setHourlyViewMode] = useState<HourlyViewMode>(HourlyViewMode.Default);

  const buildQuery = () => {
    return {
      ...getTodaysTaskCompletionsDefaultQuery(true),
      type: '',
    };
  };

  // on mount, get the tasks
  useEffect(() => {
    dispatch(getTodaysTaskCompletions(buildQuery()));
    dispatch(getLastFourHoursTasks());
    dispatch(fetchEmployees());
  }, []);

  // on primary site change, get the tasks
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(getLastFourHoursTasks());
    dispatch(getTodaysTaskCompletions(buildQuery()));
  }, [primarySite]);

  // handle local task completion and filtering
  useEffect(() => {
    const localTasks = tasks;
    if (toggledTask != null) {
      const indexById = localTasks.findIndex((t: TaskCompletion) => t.id === toggledTask);
      if (indexById != null) {
        const tempTask = localTasks[indexById];
        localTasks[indexById] = fakeComplete(tempTask);
      }
    }
    const daily = localTasks.filter((task: TaskCompletion) => task.task?.interval !== 'Hourly');
    setDailyTasks(filterTasksByType(daily));
    setHourlyTasks(localTasks.filter((task: TaskCompletion) => task.task?.interval === 'Hourly'));

    if (toggledTask != null) {
      setToggledTask(null);
      setTempEmployee(null);
    }
  }, [tasks, typeFilter, toggledTask]);

  const isTaskComplete = (tc: TaskCompletion) => {
    return tc.status === 'Completed';
  };

  const isDueToday = (tc: TaskCompletion) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const due = tc.scheduleEnd ? new Date(tc.scheduleEnd) : new Date(tc.scheduleStart);
    return due.setHours(0, 0, 0, 0) <= today;
  };

  const isTaskIncompleteToday = (tc: TaskCompletion) => {
    return tc.status !== 'Completed' && isDueToday(tc);
  };

  const isTaskIncompleteLater = (tc: TaskCompletion) => {
    return tc.status !== 'Completed' && !isDueToday(tc);
  };

  const sortByName = (a: TaskCompletion, b: TaskCompletion) => {
    return a.task.name.localeCompare(b.task.name);
  };

  const sortComplete = (a: TaskCompletion, b: TaskCompletion) => {
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  };

  const renderTask = (taskCompletion: TaskCompletion, i: number) => {
    return (
      <TaskListItem
        taskCompletion={taskCompletion}
        className="tabular"
        key={`complete-${i}`}
        toggleCompletion={toggleTaskComplete}
        displayTimeInfo={false}
      />
    );
  };

  const filterComplete = (taskList: TaskCompletion[], invert = false) => {
    if (invert) {
      return taskList.filter((task: TaskCompletion) => task.status !== 'Completed');
    } else {
      // Sort completed tasks by completion date
      return taskList
        .filter((task: TaskCompletion) => task.status === 'Completed')
        .sort((a: TaskCompletion, b: TaskCompletion) => {
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
    }
  };

  const filterTasksByType = (taskList: TaskCompletion[]) => {
    if (typeFilter === 'all') {
      return taskList;
    }
    const filtered = taskList.filter((task: TaskCompletion) => {
      const taskType = task.task?.type;
      switch (typeFilter) {
        case 'OpenChecklist':
          return taskType === 'OpenChecklist';
        case 'CleaningList':
          return taskType === 'CleaningList';
        case 'ClosingChecklist':
          return taskType === 'ClosingChecklist';
        default:
          return true;
      }
    });
    return filtered;
  };

  const toggleTaskComplete = (taskId: number, empId: number) => {
    setToggledTask(taskId);
    setTempEmployee(empId);
  };

  const fakeComplete = (task: TaskCompletion) => {
    const isComplete = task.status == 'Completed';
    task.employeeId = isComplete ? null : tempEmployee;
    task.timestamp = new Date().toISOString();
    task.status = isComplete ? 'Pending' : 'Completed';
    return task;
  };

  const setEmployeeSelectedState = (value: string) => {
    dispatch(setCurrentEmployee(Number(value)));
  };

  const handleMobileFilterChange = (e: any) => {
    if (e.target.value == 'hourly') {
      setHourlyViewMode(HourlyViewMode.Expanded);
    } else {
      setHourlyViewMode(HourlyViewMode.Collapsed);
    }
    setTypeFilter(e.target.value);
  };

  useEffect(() => {
    if (employees.length > 0 && currentEmployeeId == null) {
      dispatch(setCurrentEmployee(employees[0].id));
    }
  }, [employees]);

  return (
    <div className="todays-tasks-new">
      {employees && (
        <>
          <div className="tasks-filters">
            <div className="task-tabs">
              <button
                className={`button ${typeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setTypeFilter('all')}
              >
                <i className="uil uil-list-ul"></i>
                {intl.formatMessage({ id: 'all' })}
              </button>
              <button
                className={`button ${typeFilter === 'OpenChecklist' ? 'active' : ''}`}
                onClick={() => setTypeFilter('OpenChecklist')}
              >
                <i className="uil uil-clock"></i>
                {intl.formatMessage({ id: 'opening' })}
              </button>
              <button
                className={`button ${typeFilter === 'CleaningList' ? 'active' : ''}`}
                onClick={() => setTypeFilter('CleaningList')}
              >
                <i className="uil uil-calender"></i>
                {intl.formatMessage({ id: 'daily' })}
              </button>
              <button
                className={`button ${typeFilter === 'ClosingChecklist' ? 'active' : ''}`}
                onClick={() => setTypeFilter('ClosingChecklist')}
              >
                <i className="uil uil-keyhole-circle"></i>
                {intl.formatMessage({ id: 'closing' })}
              </button>
            </div>
            <div className="mobile-task-tabs task-filter">
              <label htmlFor="mobileTaskFilter" className="form-check-label">
                {intl.formatMessage({ id: 'taskType' })}
              </label>
              <select
                id="mobileTaskFilter"
                className="form-control"
                onChange={(e) => handleMobileFilterChange(e)}
              >
                <option value="all">{intl.formatMessage({ id: 'all' })}</option>
                <option value="hourly">{intl.formatMessage({ id: 'hourly' })}</option>
                <option value="opening">{intl.formatMessage({ id: 'opening' })}</option>
                <option value="daily">{intl.formatMessage({ id: 'daily' })}</option>
                <option value="closing">{intl.formatMessage({ id: 'closing' })}</option>
              </select>
            </div>
            <div className="task-filter">
              {employees.length > 0 && (
                <EmployeeInitialsDropdown
                  selectedEmployee={currentEmployeeId ?? employees[0]?.id}
                  onEmployeeSelect={setEmployeeSelectedState}
                  listHeight={300}
                />
              )}
            </div>
          </div>
          <div
            className={`tasks-columns${
              hourlyViewMode === HourlyViewMode.Collapsed ? ' hourly-closed' : ''
            }${hourlyViewMode === HourlyViewMode.Expanded ? ' hourly-expanded' : ''}`}
          >
            <div className="tasks-daily">
              {loadingTasks ? (
                <Loader />
              ) : (
                <div className="tasks-daily-table">
                  {/* Incomplete tasks due today */}
                  <FilterSection
                    itemList={dailyTasks}
                    renderFunction={renderTask}
                    filterFunction={isTaskIncompleteToday}
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'dueToday' })}
                  />
                  {/* Incomplete tasks due later */}
                  <FilterSection
                    itemList={dailyTasks}
                    renderFunction={renderTask}
                    filterFunction={isTaskIncompleteLater}
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'dueWithinTheWeek' })}
                    isOpen={false}
                  />
                  {/* Completed tasks */}
                  {filterComplete(dailyTasks).length > 0 && (
                    <FilterSection
                      itemList={dailyTasks}
                      renderFunction={renderTask}
                      filterFunction={isTaskComplete}
                      sortFunction={sortComplete}
                      title={intl.formatMessage({ id: 'completed' })}
                      isOpen={false}
                    />
                  )}
                </div>
              )}
            </div>
            <HourlyChecklist
              hourlyTasks={hourlyTasks}
              lastFourHoursTasks={lastFourHoursTasks}
              filterComplete={filterComplete}
              toggleTaskComplete={toggleTaskComplete}
              viewMode={hourlyViewMode}
              viewModeChange={setHourlyViewMode}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TodaysTasksNew;
